import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import FileItem from './FileItem';
import Pagination from './Pagination';
import { sortFiles } from '../utils/helpers';
import AudioPlayer from './AudioPlayer';
import apiService from '../services/apiService';

const FileList = ({
  files = [],
  folders = [],
  isLoading,
  currentFolder,
  onFolderClick,
  sortOption,
  currentPage,
  itemsPerPage,
  totalItems,
  onPageChange,
  showFoldersOnly = false,
  onSortChange,
  currentSortOption
}) => {
  // Sort folders and files based on sort option
  const sortedFolders = [...folders].sort((a, b) => a.name.localeCompare(b.name));
  const sortedFiles = sortFiles([...files], sortOption);
  
  // Log the current pagination state
  useEffect(() => {
    console.log('FileList pagination:', { 
      currentPage, 
      itemsPerPage, 
      totalItems,
      pagesNeeded: Math.ceil(totalItems / itemsPerPage),
      showingItems: showFoldersOnly ? sortedFolders.length : sortedFiles.length,
      totalAvailableItems: showFoldersOnly ? folders.length : files.length
    });
  }, [currentPage, itemsPerPage, totalItems, folders.length, files.length, showFoldersOnly, sortedFolders.length, sortedFiles.length]);
  
  // Add state for currently playing file and audio URL
  const [playingFile, setPlayingFile] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [audioLoading, setAudioLoading] = useState(false);

  // Play handler
  const handlePlay = async (file) => {
    // Stop any currently playing audio
    if (window && window._audioPlayerInstances) {
      window._audioPlayerInstances.forEach(fn => typeof fn === 'function' && fn());
    }
    if (playingFile && playingFile.name === file.name) {
      setPlayingFile(null);
      setAudioUrl(null);
      return;
    }
    setAudioLoading(true);
    try {
      const response = await apiService.getFileDownloadUrl(file.name);
      const url = typeof response === 'string' ? response : response.url;
      setPlayingFile(file);
      setAudioUrl(url);
    } catch (e) {
      alert('Failed to load audio.');
      setPlayingFile(null);
      setAudioUrl(null);
    } finally {
      setAudioLoading(false);
    }
  };

  // If loading, show loading indicator
  if (isLoading) {
    return (
      <LoadingContainer>
        <i className="fas fa-spinner fa-spin"></i>
        <span>Loading {showFoldersOnly ? 'folders' : 'files'}...</span>
      </LoadingContainer>
    );
  }

  // Check if we have items to display
  const hasItems = showFoldersOnly ? folders.length > 0 : files.length > 0;

  // If no items, show empty state
  if (!hasItems) {
    return (
      <EmptyContainer>
        <i className="fas fa-folder-open"></i>
        <h3>No {showFoldersOnly ? 'folders' : 'files'} found</h3>
        <p>
          {showFoldersOnly 
            ? 'This location does not contain any subfolders.' 
            : 'This folder is empty or does not contain any files.'}
        </p>
      </EmptyContainer>
    );
  }

  // Calculate total pages
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  
  // Calculate pagination indexes
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  
  // Get items for current page (either folders or files, not both)
  const itemsToDisplay = showFoldersOnly 
    ? sortedFolders.slice(startIndex, endIndex) 
    : sortedFiles.slice(startIndex, endIndex);

  // Force at least 1 totalPage if we have any items
  const effectiveTotalPages = Math.max(1, totalPages);

  console.log(`Rendering ${itemsToDisplay.length} items, page ${currentPage} of ${effectiveTotalPages}, totalItems: ${totalItems}, itemsPerPage: ${itemsPerPage}`);

  // Helper to get sort arrows (always visible)
  const getSortArrows = (col) => {
    const isActive = currentSortOption && currentSortOption.startsWith(col);
    const isAsc = isActive && currentSortOption.endsWith('asc');
    const isDesc = isActive && currentSortOption.endsWith('desc');
    return (
      <span style={{ marginLeft: 6, fontSize: 13, display: 'inline-block', verticalAlign: 'middle' }}>
        <span style={{ color: isAsc ? '#001a36' : '#bbb', fontWeight: isAsc ? 700 : 400 }}>▲</span>
        <span style={{ color: isDesc ? '#001a36' : '#bbb', fontWeight: isDesc ? 700 : 400, marginLeft: -2 }}>▼</span>
      </span>
    );
  };

  // Helper to get header style
  const getHeaderStyle = (col) => {
    if (currentSortOption && currentSortOption.startsWith(col)) {
      return {
        background: '#f0f6ff',
        fontWeight: 700,
        color: '#001a36',
        cursor: 'pointer',
        transition: 'background 0.2s',
      };
    }
    return { cursor: 'pointer', transition: 'background 0.2s' };
  };

  // Handle header click
  const handleHeaderClick = (col) => {
    let direction = 'asc';
    if (currentSortOption && currentSortOption.startsWith(col)) {
      direction = currentSortOption.endsWith('asc') ? 'desc' : 'asc';
    }
    if (onSortChange) onSortChange(`${col}-${direction}`);
  };

  return (
    <FileListContainer>
      {/* Table always visible */}
      <TableWrapper>
        <StyledTable>
          <thead>
            <tr>
              <th style={getHeaderStyle('name')} onClick={() => handleHeaderClick('name')}>
                Name{getSortArrows('name')}
              </th>
              <th style={getHeaderStyle('type')} onClick={() => handleHeaderClick('type')}>
                Type{getSortArrows('type')}
              </th>
              <th style={getHeaderStyle('size')} onClick={() => handleHeaderClick('size')}>
                Size{getSortArrows('size')}
              </th>
              <th style={{background: '#fafbfc', color: '#001a36', fontWeight: 600}}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {showFoldersOnly ? (
              itemsToDisplay.map((folder, index) => (
                <FileItem 
                  key={`folder-${index}`}
                  file={folder}
                  isFolder={true}
                  onFolderClick={onFolderClick}
                  currentFolder={currentFolder}
                  as="tr"
                />
              ))
            ) : (
              itemsToDisplay.flatMap((file, index) => {
                const isPlaying = playingFile && playingFile.name === file.name;
                return [
                  <FileItem
                    key={`file-${index}`}
                    file={file}
                    isFolder={false}
                    currentFolder={currentFolder}
                    onPlay={handlePlay}
                    isPlaying={isPlaying}
                    isLoading={audioLoading && isPlaying}
                  />,
                  isPlaying && audioUrl ? (
                    <tr key={`player-${index}`} className="audio-player-row">
                      <td colSpan={4} style={{
                        padding: '20px 16px',
                        background: '#f8f9fc',
                        borderTop: '1px solid #e8eaf0',
                        borderBottom: '1px solid #e8eaf0'
                      }}>
                        <AudioPlayerWrapper>
                          <AudioPlayer src={audioUrl} fileName={file.name} onClose={() => { setPlayingFile(null); setAudioUrl(null); }} />
                        </AudioPlayerWrapper>
                      </td>
                    </tr>
                  ) : null
                ];
              })
            )}
          </tbody>
        </StyledTable>
      </TableWrapper>
      {totalItems > 0 && (
        <PaginationWrapper>
          <Pagination
            currentPage={currentPage}
            totalItems={totalItems}
            itemsPerPage={itemsPerPage}
            onPageChange={onPageChange}
          />
        </PaginationWrapper>
      )}
    </FileListContainer>
  );
};

// Styled Components
const FileListContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  animation: fadeIn 0.3s ease;
  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(20px); }
    to { opacity: 1; transform: translateY(0); }
  }
`;

const TableWrapper = styled.div`
  width: 100%;
  overflow-x: auto;
`;

const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  background: white;
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0,0,0,0.04);

  th, td {
    padding: 12px 16px;
    text-align: left;
    border-bottom: 1px solid #f0f0f0;
    vertical-align: middle;
  }
  th {
    background: #fafbfc;
    font-weight: 600;
    color: #001a36;
    font-size: 15px;
  }
  tr:last-child td {
    border-bottom: none;
  }
  
  .audio-player-row {
    animation: fadeInRow 0.3s ease-in-out;
  }
  
  @keyframes fadeInRow {
    from { opacity: 0; }
    to { opacity: 1; }
  }
`;

const AudioPlayerWrapper = styled.div`
  width: 100%;
  border-radius: 8px;
  background: white;
  padding: 16px;
  box-shadow: 0 2px 12px rgba(0,0,0,0.08);
`;

const PaginationWrapper = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
  margin-top: 20px;
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 50px 0;
  color: var(--text-light);
  animation: fadeIn 0.3s ease;
  
  i {
    font-size: 40px;
    margin-bottom: 20px;
    color: var(--secondary-color);
  }
`;

const EmptyContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 50px 0;
  color: var(--text-light);
  text-align: center;
  animation: fadeIn 0.3s ease;
  
  i {
    font-size: 48px;
    margin-bottom: 20px;
    color: var(--border-color);
  }
  
  h3 {
    margin-bottom: 10px;
    color: var(--text-color);
  }
  
  p {
    max-width: 300px;
  }
`;

export default FileList; 